import { z } from 'zod';

/**
 * BP-21 §5's wire shapes, parsed at the edge.
 *
 * `fps_avg` crosses as a string and stays one until it is sent back. It is a
 * `Decimal` on the server because rule 5's threshold is compared against it,
 * and a screen that re-encoded it through a float would eventually report
 * 7.999999 for a device that managed 8.
 *
 * `srgb_hex` carries no `#`. BP-12 stores it that way — the hash belongs to
 * CSS, not to the colour — and `cssColour` is the one place it is added.
 */

export const AR_MODES = ['phone', 'tablet'] as const;
export type ArMode = (typeof AR_MODES)[number];

export const shadeSchema = z.object({
  sku_id: z.string(),
  shade_name: z.string(),
  shade_code: z.string().nullable(),
  finish: z.string(),
  srgb_hex: z.string(),
  is_indicative: z.boolean(),
});
export type Shade = z.infer<typeof shadeSchema>;
export const shadeListSchema = z.array(shadeSchema);

export const sessionSchema = z.object({
  id: z.string(),
  client_id: z.string().nullable(),
  mode: z.enum(AR_MODES),
  device_class: z.string().nullable(),
  fps_avg: z.string().nullable(),
  model_version_id: z.string().nullable(),
  shades_tried: z.array(z.string()),
  booking_id: z.string().nullable(),
  started_at: z.string(),
  ended_at: z.string().nullable(),
  below_usable_fps: z.boolean(),
});
export type TryOnSession = z.infer<typeof sessionSchema>;

export const conversionSchema = z.object({
  sessions: z.number().int(),
  with_a_shade: z.number().int(),
  booked: z.number().int(),
  below_usable_fps: z.number().int(),
  rate: z.string(),
});
export type Conversion = z.infer<typeof conversionSchema>;

/** `srgb_hex` as a CSS colour. The one place the hash is added. */
export function cssColour(shade: Pick<Shade, 'srgb_hex'>): string {
  return `#${shade.srgb_hex}`;
}

// --------------------------------------------------------------------------- //
// BP-22 §5.                                                                    //
// --------------------------------------------------------------------------- //

export const RENDER_STATUSES = ['queued', 'rendering', 'done', 'failed', 'expired'] as const;
export type RenderStatus = (typeof RENDER_STATUSES)[number];

export const RENDER_FAILURES = [
  'no_model',
  'unreadable',
  'too_large',
  'no_hand',
  'internal',
] as const;
export type RenderFailure = (typeof RENDER_FAILURES)[number];

/**
 * One render.
 *
 * There is no `input_key`: rule 1's photograph is about to be deleted and
 * a key on the wire is a key somebody can be persuaded to fetch. The api
 * does not send one, and this would reject it if it did.
 */
export const renderSchema = z.object({
  id: z.string(),
  client_id: z.string().nullable(),
  session_id: z.string().nullable(),
  status: z.enum(RENDER_STATUSES),
  failure: z.enum(RENDER_FAILURES).nullable(),
  output_key: z.string().nullable(),
  shades: z.array(z.string()),
  design_id: z.string().nullable(),
  model_version_id: z.string().nullable(),
  refined: z.boolean(),
  expires_at: z.string(),
});
export type Render = z.infer<typeof renderSchema>;

export const designSchema = z.object({
  id: z.string(),
  name: z.string(),
  decal_key: z.string(),
  uv_template: z.string(),
  price_paise: z.number().int(),
  duration_min: z.number().int(),
  addon_id: z.string(),
  is_active: z.boolean(),
  withdrawn_on: z.string().nullable(),
});
export type NailArtDesign = z.infer<typeof designSchema>;
export const designListSchema = z.array(designSchema);

/** Whether a render has stopped moving, so the screen can stop asking. */
export function isSettled(render: Render): boolean {
  return render.status !== 'queued' && render.status !== 'rendering';
}
